import { useEffect, useState } from "react";

import type {
  DashboardLayoutProps,
  SidebarCollapsedEventDetail,
} from "../types/dashboardLayout.types";
import type { MenuName } from "../types/sidebar.types";

type UseDashboardLayoutHookParams = Pick<DashboardLayoutProps, "children">;

const getStoredSidebarCollapsed = () => {
  if (typeof window === "undefined") return false;
  return localStorage.getItem("sidebarCollapsed") === "1";
};

const getIsMobile = () => {
  if (typeof window === "undefined") return false;
  return window.innerWidth < 768;
};

export const useDashboardLayoutHook = ({}: UseDashboardLayoutHookParams) => {
  const [activeMenuItem, setActiveMenuItem] =
    useState<MenuName>("User Management");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(
    getStoredSidebarCollapsed,
  );

  const sidebarLeft = 18;
  const widthExpandedMd = 200;
  const widthCollapsedMd = 72;

  useEffect(() => {
    setMounted(true);
    setIsMobile(getIsMobile());
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(getIsMobile());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleSidebarCollapsed = (event: Event) => {
      const detail = (event as CustomEvent<SidebarCollapsedEventDetail>).detail;

      if (detail && typeof detail.collapsed === "boolean") {
        setSidebarCollapsed(detail.collapsed);
      }
    };

    window.addEventListener(
      "sidebar:collapsed",
      handleSidebarCollapsed as EventListener,
    );

    return () => {
      window.removeEventListener(
        "sidebar:collapsed",
        handleSidebarCollapsed as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  const contentMarginLeftMd = sidebarCollapsed
    ? `${sidebarLeft + widthCollapsedMd}px`
    : `${sidebarLeft + widthExpandedMd}px`;

  const handleDrawerToggle = () => {
    if (!mounted) return;
    setMobileOpen((prev) => !prev);
  };

  const handleMenuItemChange = (item: MenuName) => {
    if (!mounted) return;
    setActiveMenuItem(item);
  };

  const handleSidebarItemClick = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  return {
    mounted,
    isMobile,
    mobileOpen,
    activeMenuItem,
    contentMarginLeftMd,
    sidebarLeft,
    handleDrawerToggle,
    handleMenuItemChange,
    handleSidebarItemClick,
  };
};
